import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  FaCheck,
  FaEnvelope,
  FaBox,
  FaShippingFast,
  FaClock,
  FaCalendarAlt,
  FaCreditCard,
  FaMoneyBillWave,
  FaBuilding,
  FaFileInvoice,
  FaVideo,
  FaMapMarkerAlt,
} from "react-icons/fa";
import SmallClouds from "../../components/utils/SmallClouds";
import {
  PageWrapper,
  Container,
  SuccessContainer,
  SuccessIcon,
  SuccessTitle,
  SuccessSubtitle,
  InfoGrid,
  InfoBox,
  InfoHeader,
  InfoTitle,
  InfoText,
  RedirectNotice,
  RedirectText,
  OrderNumber,
  ManualRedirectButton,
} from "./PaymentSuccess.styles";
import { useCart } from "../../store/CartContext";
import paymentsService from "../../api/services/payments";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { formatDate } from "../../utils/utils";

// Notify iz banke može da stigne par sekundi POSLE redirecta na ovu stranu,
// pa "pending" status proveravamo još nekoliko puta pre nego što odustanemo.
const STATUS_POLL_ATTEMPTS = 5;
const STATUS_POLL_INTERVAL_MS = 3000;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const loggedIn = useSelector(selectLoggedIn);
  const { clearCart } = useCart();

  const [count, setCount] = useState(10);
  const [orderStatus, setOrderStatus] = useState(null);
  const [statusChecked, setStatusChecked] = useState(false);
  const cartCleared = useRef(false);

  const orderNumber = searchParams.get("order");
  const orderType = searchParams.get("type") || "purchase"; // 'workshop', 'purchase', or 'mixed'
  const paymentMethod = searchParams.get("method") || "card"; // 'card', 'bank', 'cash', 'pickup'

  const isWorkshop = orderType === "workshop";
  // Gost (rezervacija radionice bez naloga) nema korisnički panel, pa nema
  // automatskog preusmeravanja; umesto toga dugme nazad na radionice.
  const autoRedirect = loggedIn;

  // Korpu praznimo TEK kad potvrdimo da porudžbina zaista postoji, inače bi
  // slučajan/obeležen /placanje-uspesno link brisao korpu usred kupovine, a i
  // lažni "uspeh" bi se prikazivao za bilo koji broj. Isti poziv vraća i termin
  // radionice (i gostu, bez ličnih podataka) za poruku ispod.
  useEffect(() => {
    let active = true;
    let attempts = 0;
    let timer;

    if (!orderNumber) {
      setStatusChecked(true);
      return undefined;
    }

    const check = () => {
      paymentsService
        .checkOrderStatus(orderNumber)
        .then((response) => {
          if (!active) return;
          const order = response?.data?.order || null;
          setOrderStatus(order);

          if (order && !cartCleared.current) {
            cartCleared.current = true;
            clearCart();
          }

          if (order?.status === "pending" && attempts < STATUS_POLL_ATTEMPTS) {
            attempts += 1;
            timer = setTimeout(check, STATUS_POLL_INTERVAL_MS);
          } else {
            setStatusChecked(true);
          }
        })
        .catch(() => {
          /* porudžbina ne postoji / nije korisnikova, ne diramo korpu */
          if (active) setStatusChecked(true);
        });
    };

    check();

    return () => {
      active = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line
  }, [orderNumber]);

  useEffect(() => {
    if (!autoRedirect || count === 0) return undefined;
    const timer = setTimeout(() => setCount(count - 1), 1000);
    return () => clearTimeout(timer);
  }, [count, autoRedirect]);

  // ✅ Auto redirect based on order type (samo prijavljeni korisnici)
  useEffect(() => {
    if (autoRedirect && count === 0) {
      const redirectUrl = isWorkshop
        ? "/korisnicki-panel/moje-radionice"
        : "/korisnicki-panel/moji-setovi";

      window.location.href = redirectUrl;
    }
  }, [count, autoRedirect, isWorkshop]);

  const getPaymentMethodInfo = () => {
    switch (paymentMethod) {
      case "bank":
        return {
          icon: FaFileInvoice,
          title: "Plaćanje uplatnicom",
          text: "Molimo izvršite uplatu prema uplatnici. Setovi će biti aktivirani nakon potvrde uplate (1-2 radna dana).",
          statusNote: "Čeka se uplata",
        };
      case "cash":
        return {
          icon: FaMoneyBillWave,
          title: "Plaćanje pouzećem",
          text: "Platite kuriru prilikom preuzimanja. Setovi će biti aktivirani nakon potvrde uplate.",
          statusNote: "Čeka se potvrda uplate",
        };
      case "pickup":
        return {
          icon: FaBuilding,
          title: "Lično preuzimanje",
          text: 'Kontaktiraćemo vas za dogovor oko preuzimanja u vrtiću "Dobra Vila". Setovi će biti aktivirani nakon potvrde uplate.',
          statusNote: "Čeka se potvrda uplate",
        };
      case "card":
      default:
        return {
          icon: FaCreditCard,
          title: "Plaćanje karticom",
          text: "Plaćanje je uspešno obrađeno. Setovi su odmah dostupni u vašem nalogu!",
          statusNote: "Plaćeno - odmah dostupno",
        };
    }
  };

  // ─── Rezervacija radionice (klijentski zahtev: jasna poruka bez naloga) ───
  const renderWorkshopContent = () => {
    const workshop = orderStatus?.workshops?.[0] || null;
    const isOnline = workshop?.type === "online";
    const isLive = workshop?.type === "live";
    const stillProcessing = !statusChecked && orderStatus?.status === "pending";
    const processedLater = statusChecked && orderStatus?.status === "pending";

    const workshopLabel = workshop
      ? ` "${workshop.title}" (${formatDate(workshop.date)}${
          workshop.time ? ` u ${String(workshop.time).slice(0, 5)}h` : ""
        })`
      : "";

    return (
      <>
        <SuccessTitle>
          {stillProcessing ? "Obrađujemo uplatu..." : "Mesto je rezervisano!"}
        </SuccessTitle>

        {orderNumber && <OrderNumber>Rezervacija #{orderNumber}</OrderNumber>}

        <SuccessSubtitle>
          {stillProcessing
            ? "Banka je potvrdila uplatu, upisujemo vašu rezervaciju."
            : `Hvala vam! Vaše mesto na radionici${workshopLabel} je rezervisano.`}
          {processedLater &&
            " Uplata se još obrađuje, potvrdu na email dobijate u narednih nekoliko minuta."}
        </SuccessSubtitle>

        <InfoGrid>
          <InfoBox>
            <InfoHeader>
              <FaEnvelope />
              <InfoTitle>Email potvrda</InfoTitle>
            </InfoHeader>
            <InfoText>
              Na email koji ste uneli poslali smo potvrdu sa detaljima
              radionice.
            </InfoText>
          </InfoBox>

          <InfoBox>
            <InfoHeader>
              {isLive ? <FaMapMarkerAlt /> : <FaVideo />}
              <InfoTitle>
                {isLive ? "Mesto održavanja" : "Link za online radionicu"}
              </InfoTitle>
            </InfoHeader>
            <InfoText>
              {isLive
                ? `Radionica se održava uživo${
                    workshop?.location ? ` (${workshop.location})` : ""
                  }. Detalji su u potvrdi na emailu.`
                : isOnline
                  ? "Link za Zoom radionicu šaljemo na isti email pred početak."
                  : "Ako je radionica online, link šaljemo na email pred početak."}
            </InfoText>
          </InfoBox>

          <InfoBox>
            <InfoHeader>
              <FaCalendarAlt />
              <InfoTitle>Vaše mesto</InfoTitle>
            </InfoHeader>
            <InfoText>
              Plaćeno karticom. Mesto je sačuvano za vas.
              <br />
              {loggedIn
                ? "Rezervaciju vidite i u korisničkom panelu, pod Moje radionice."
                : "Nalog vam nije potreban, sve informacije stižu na email."}
            </InfoText>
          </InfoBox>
        </InfoGrid>

        {autoRedirect ? (
          <RedirectNotice>
            <RedirectText>
              <FaClock />
              Automatski ćete biti preusmeren na Moje radionice za {count}{" "}
              sekunde...
            </RedirectText>
          </RedirectNotice>
        ) : (
          <ManualRedirectButton
            type="button"
            onClick={() => {
              navigate("/recepti-za-roditeljstvo");
              window.scrollTo(0, 0);
            }}
          >
            Nazad na radionice
          </ManualRedirectButton>
        )}
      </>
    );
  };

  const getContentByOrderType = () => {
    const paymentInfo = getPaymentMethodInfo();

    if (orderType === "mixed") {
      return {
        title:
          paymentMethod === "bank" ||
          paymentMethod === "cash" ||
          paymentMethod === "pickup"
            ? "Narudžbina primljena!"
            : "Plaćanje uspešno!",
        subtitle:
          paymentMethod === "bank"
            ? "Vaša narudžbina je primljena. Molimo izvršite uplatu prema uplatnici. Setovi i radionice će biti aktivirani nakon potvrde uplate."
            : paymentMethod === "cash"
              ? "Hvala vam na poverenju! Vaša narudžbina je primljena. Setovi i radionice će biti aktivirani nakon potvrde uplate prilikom dostave."
              : paymentMethod === "pickup"
                ? "Hvala vam na poverenju! Vaša narudžbina je primljena. Setovi i radionice će biti aktivirani nakon potvrde uplate prilikom preuzimanja."
                : "Hvala vam na poverenju! Vaše plaćanje je uspešno obrađeno. Setovi i radionice su odmah dostupni!",
        deliveryInfo: {
          icon: FaShippingFast,
          title: "Dostava i radionice",
          text:
            paymentMethod === "bank"
              ? "Setovi će biti isporučeni nakon potvrde uplate (1-2 radna dana)"
              : paymentMethod === "pickup"
                ? "Dogovorićemo termin preuzimanja telefonom"
                : "Setovi će biti isporučeni u roku od 1-2 radna dana",
        },
        paymentInfo,
        redirectText: "Automatski ćete biti preusmeren na korisnički panel",
        redirectPath: "/korisnicki-panel/moji-setovi",
      };
    }

    // Default: purchase
    return {
      title:
        paymentMethod === "bank" ||
        paymentMethod === "cash" ||
        paymentMethod === "pickup"
          ? "Narudžbina primljena!"
          : "Plaćanje uspešno!",
      subtitle:
        paymentMethod === "bank"
          ? "Vaša narudžbina je primljena. Molimo izvršite uplatu prema uplatnici. Setovi će biti aktivirani nakon potvrde uplate (1-2 radna dana)."
          : paymentMethod === "cash"
            ? "Hvala vam na poverenju! Vaša narudžbina je primljena. Setovi će biti aktivirani nakon potvrde uplate prilikom dostave."
            : paymentMethod === "pickup"
              ? "Hvala vam na poverenju! Vaša narudžbina je primljena. Setovi će biti aktivirani nakon potvrde uplate prilikom preuzimanja."
              : "Hvala vam na poverenju! Vaše plaćanje je uspešno obrađeno. Setovi su odmah dostupni u vašem nalogu!",
      deliveryInfo: {
        icon: FaShippingFast,
        title: "Dostava",
        text:
          paymentMethod === "bank"
            ? "Narudžbina će biti poslata nakon potvrde uplate (1-2 radna dana)"
            : paymentMethod === "pickup"
              ? "Dogovorićemo termin preuzimanja telefonom"
              : "Narudžbina će biti obrađena u roku od 1-2 radna dana",
      },
      paymentInfo,
      redirectText: "Automatski ćete biti preusmeren na Moji setovi",
      redirectPath: "/korisnicki-panel/moji-setovi",
    };
  };

  const renderOrderContent = () => {
    const content = getContentByOrderType();

    return (
      <>
        <SuccessTitle>{content.title}</SuccessTitle>

        {orderNumber && <OrderNumber>Narudžbina #{orderNumber}</OrderNumber>}

        <SuccessSubtitle>{content.subtitle}</SuccessSubtitle>

        <InfoGrid>
          <InfoBox>
            <InfoHeader>
              <FaEnvelope />
              <InfoTitle>Email potvrda</InfoTitle>
            </InfoHeader>
            <InfoText>
              Potvrda narudžbine
              {paymentMethod === "bank" && " i instrukcije za plaćanje"} je
              poslata na vašu email adresu
            </InfoText>
          </InfoBox>

          <InfoBox>
            <InfoHeader>
              <content.paymentInfo.icon />
              <InfoTitle>{content.paymentInfo.title}</InfoTitle>
            </InfoHeader>
            <InfoText>{content.paymentInfo.text}</InfoText>
          </InfoBox>

          <InfoBox>
            <InfoHeader>
              <FaBox />
              <InfoTitle>Status</InfoTitle>
            </InfoHeader>
            <InfoText>
              {content.paymentInfo.statusNote}
              <br />
              Možete pratiti status u korisničkom panelu
            </InfoText>
          </InfoBox>
        </InfoGrid>

        {autoRedirect ? (
          <RedirectNotice>
            <RedirectText>
              <FaClock />
              {content.redirectText} za {count} sekunde...
            </RedirectText>
          </RedirectNotice>
        ) : (
          <ManualRedirectButton
            type="button"
            onClick={() => {
              navigate("/");
              window.scrollTo(0, 0);
            }}
          >
            Nazad na početnu
          </ManualRedirectButton>
        )}
      </>
    );
  };

  return (
    <PageWrapper>
      <SmallClouds experiences />

      <Container>
        <SuccessContainer>
          <SuccessIcon>
            <FaCheck />
          </SuccessIcon>

          {isWorkshop ? renderWorkshopContent() : renderOrderContent()}
        </SuccessContainer>
      </Container>
    </PageWrapper>
  );
}
